import { ReactNode } from 'react';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';

import Icon from 'lib/common/components/Icon';
import Text from 'lib/common/components/Text';
import { Colour } from 'lib/common/components/Text/types';

import getPanelProps from './constants/panelProps';
import PanelTypes, { TPanelTypes } from './types/PanelTypes';

import COLOURS from 'css/export-vars.module.scss';

import styles from './info-panel.module.scss';

const ICON_SIZE = 17;

export interface IInfoPanel {
  icon?: IconDefinition;
  children: ReactNode | string;
  headerTitle?: string;
  type?: TPanelTypes;
  className?: string;
  tooltip?: boolean;
  id?: string;
  infoTooltip?: string;
  noStroke?: true;
}

export default function InfoPanel({
  icon,
  headerTitle,
  children,
  type = PanelTypes.INFO,
  className,
  tooltip,
  id,
  infoTooltip,
  noStroke,
}: IInfoPanel) {
  const { background, highlightColorName, iconColorName, textColorName, defaultIcon } = getPanelProps()[type];

  const panel = (
    <div
      className={cx(styles['info-panel'], { [className || '']: !tooltip })}
      style={{
        background: background,
        borderLeft: noStroke ? 'transparent' : `3px solid ${COLOURS[highlightColorName]}`,
        color: COLOURS[textColorName] || COLOURS.black,
      }}
      data-testid={id || 'info-panel'}
    >
      <div className={cx(styles['info-panel__inner'], { [styles['info-panel__inner--no-stroke']]: noStroke })}>
        <div
          className={cx(styles['info-panel__inner__icon'], {
            [styles['info-panel__inner__icon--with-title']]: Boolean(headerTitle),
          })}
        >
          <Icon icon={icon || defaultIcon} size={ICON_SIZE} color={iconColorName} spin={type === PanelTypes.LOADING} />
        </div>
        <div className={styles['info-panel__container']}>
          {headerTitle && (
            <div className={cx(styles['info-panel__container__header'], 'mb-5')}>
              <Text type="heading3" color={textColorName as Colour}>
                {headerTitle}
              </Text>
              {infoTooltip && (
                <Icon
                  icon={faCircleQuestion}
                  size={ICON_SIZE}
                  color="darkGrey"
                  className="ml-10"
                  tooltip={infoTooltip}
                  tooltipPlacement="top"
                />
              )}
            </div>
          )}
          {typeof children === 'string' ? <Text color={textColorName as Colour}>{children}</Text> : children}
        </div>
      </div>
    </div>
  );

  if (!tooltip) {
    return panel;
  }

  return (
    <div className={cx(styles['info-panel-with-tooltip'], className)}>
      {panel}
      {tooltip && (
        <div
          className={styles['info-panel__arrow']}
          style={{
            borderColor: background,
          }}
        />
      )}
    </div>
  );
}
